import _ from 'lodash';
import {
  CruiseQueryStringFilterKeys, SearchModes,
  TourQueryStringFilterKeys,
  CustomInventoryQueryStringFilterKeys,
  FiltersInitialValue,
} from '../constants';
import encodeAddressAutocompleteObj from './urlEncode/encodeAddressAutocompleteObj';
import encodeGeonameObj from './urlEncode/encodeGeonameObj';
import encodeLocationTypeObj from './urlEncode/encodeLocationTypeObj';
import encodeStateroomsObj from './urlEncode/encodeStateroomsObj';
import encodeGeonameAirportObj from './urlEncode/encodeGeonameAirportObj';

export default function normalizeSearchObjForQueryString({
  searchObj, searchedMode,
}) {
  if (searchedMode === SearchModes.CRUISE_TRIP) {
    const cruiseSearchObj = searchObj.cruise;
    const copiedSearchObj = {
      ...cruiseSearchObj,
      filters: {
        ...cruiseSearchObj.filters,
      },
    };
    const filteredSearchObject = _.pick(copiedSearchObj, CruiseQueryStringFilterKeys);

    // remove empty staterooms
    _.set(filteredSearchObject, 'staterooms', encodeStateroomsObj(searchObj.staterooms));

    // remove unnecessary origin properties
    if (filteredSearchObject.filters.origin) {
      _.set(filteredSearchObject, 'filters.origin', encodeGeonameObj(filteredSearchObject.filters.origin));
    }
    // remove unnecessary city properties
    if (filteredSearchObject.filters.city) {
      _.set(filteredSearchObject, 'filters.city', encodeAddressAutocompleteObj(filteredSearchObject.filters.city));
    }
    return filteredSearchObject;
  }

  if (searchedMode === SearchModes.HOTEL) {
    const filteredSearchObject = {
      ...searchObj.hotel,
    };

    // remove empty staterooms
    _.set(filteredSearchObject, 'staterooms', encodeStateroomsObj(searchObj.staterooms));

    // remove unnecessary properties
    if (filteredSearchObject.selectedAddress) {
      _.set(filteredSearchObject, 'selectedAddress', encodeAddressAutocompleteObj(filteredSearchObject.selectedAddress));
    }

    // remove unnecessary origin properties
    if (filteredSearchObject.origin) {
      _.set(filteredSearchObject, 'origin', encodeGeonameObj(filteredSearchObject.origin));
    }

    _.set(filteredSearchObject, 'fp', filteredSearchObject.fromPrice);
    _.set(filteredSearchObject, 'tp', filteredSearchObject.toPrice);

    if (!filteredSearchObject.doShowCommissionableRatesOnly) {
      delete filteredSearchObject.doShowCommissionableRatesOnly;
    }
    if (!filteredSearchObject.showRefundableHotelRatesOnly) {
      delete filteredSearchObject.showRefundableHotelRatesOnly;
    }
    if (!filteredSearchObject.doShowAvailableHotelsOnly) {
      filteredSearchObject.showUnavailableHotels = true;
    } else {
      filteredSearchObject.showUnavailableHotels = undefined;
    }
    if (!filteredSearchObject.filterRating) {
      delete filteredSearchObject.filterRating;
    }

    if (_.isUndefined(filteredSearchObject.selectedCurrency)
    || _.isNull(filteredSearchObject.selectedCurrency)) {
      delete filteredSearchObject.selectedCurrency;
    }
    delete filteredSearchObject.fromPrice;
    delete filteredSearchObject.toPrice;
    delete filteredSearchObject.doShowAvailableHotelsOnly;

    return filteredSearchObject;
  }

  if (searchedMode === SearchModes.FLIGHT) {
    const filteredSearchObject = {
      ...searchObj.flight,
    };
    delete filteredSearchObject.airlinesAutocompleteResults;
    delete filteredSearchObject.defaultSearchObj;

    // remove empty staterooms
    _.set(filteredSearchObject, 'staterooms', encodeStateroomsObj(searchObj.staterooms));

    const flightSegs = [...filteredSearchObject.flightSegments];
    for (let index = 0; index < filteredSearchObject.flightSegments.length; index += 1) {
      flightSegs[index] = {
        ...filteredSearchObject.flightSegments[index],
        origin: {
          ...encodeGeonameObj(flightSegs[index].origin),
        },
        destination: {
          ...encodeGeonameObj(flightSegs[index].destination),
        },
      };
      if (!flightSegs[index].airlines || !flightSegs[index].airlines.length) {
        delete flightSegs[index].airlines;
      }

      if (flightSegs[index].alliances && flightSegs[index].alliances.length > 0) {
        flightSegs[index] = {
          ...flightSegs[index],
          alliances: _.map(flightSegs[index].alliances, (a) => a.value).join(','),
        };
      } else {
        delete flightSegs[index].alliances;
      }

      if (!flightSegs[index].departureTimeWindow) {
        delete flightSegs[index].departureTimeWindow;
      }
      if (!flightSegs[index].arrivalTimeWindow) {
        delete flightSegs[index].arrivalTimeWindow;
      }

      if (flightSegs[index].connectingAirports && flightSegs[index].connectingAirports.length > 0) {
        flightSegs[index] = {
          ...flightSegs[index],
          connectingAirports: _.map(flightSegs[index].connectingAirports,
            (a) => encodeGeonameAirportObj(a)),
        };
        if (flightSegs[index].connectingAirportsPreference === 0) {
          delete flightSegs[index].connectingAirportsPreference;
        }
      } else {
        delete flightSegs[index].connectingAirports;
        delete flightSegs[index].connectingAirportsPreference;
      }
    }
    delete filteredSearchObject.flightSegments;
    filteredSearchObject.fs = [...flightSegs];
    if (filteredSearchObject.hasDifferentOutboundInboundAirLine === true
      && _.some(filteredSearchObject.fs, (f) => f.airlines && f.airlines.length)) {
      filteredSearchObject.diffAirlines = filteredSearchObject.hasDifferentOutboundInboundAirLine;
    }
    if (filteredSearchObject.hasDifferentOutboundInboundAlliance === true
      && _.some(filteredSearchObject.fs, (f) => f.alliances && f.alliances.length)) {
      filteredSearchObject.diffAlliances = filteredSearchObject.hasDifferentOutboundInboundAlliance;
    }
    if (filteredSearchObject.hasDifferentOutboundInboundCabins === true) {
      filteredSearchObject.diffCabins = filteredSearchObject.hasDifferentOutboundInboundCabins;
    }
    if (filteredSearchObject.hasDifferentOutboundInboundConnectingAirports === true) {
      filteredSearchObject.diffConnectingAirports = filteredSearchObject
        .hasDifferentOutboundInboundConnectingAirports;
    }
    if (!filteredSearchObject.exactMatchRateCodesOnly) {
      delete filteredSearchObject.exactMatchRateCodesOnly;
    }
    filteredSearchObject.fst = filteredSearchObject.flightSearchType;
    if (filteredSearchObject.avoidCabinDowngrade
       === FiltersInitialValue.flight.avoidCabinDowngrade) {
      delete filteredSearchObject.avoidCabinDowngrade;
    }
    if (filteredSearchObject.keepSameCabin === FiltersInitialValue.flight.keepSameCabin) {
      delete filteredSearchObject.keepSameCabin;
    }
    if (filteredSearchObject.maxNumOfStops === null || filteredSearchObject.maxNumOfStops === '') delete filteredSearchObject.maxNumOfStops;
    if (filteredSearchObject.airlinesPreference === null || filteredSearchObject.airlinesPreference === undefined || filteredSearchObject.airlinesPreference === 0 || filteredSearchObject.airlinesPreference === '') delete filteredSearchObject.airlinesPreference;
    if (filteredSearchObject.alliancePreference === null || filteredSearchObject.alliancePreference === undefined || filteredSearchObject.alliancePreference === 0 || filteredSearchObject.alliancePreference === '') delete filteredSearchObject.alliancePreference;
    if (filteredSearchObject.alliances && filteredSearchObject.alliances.length > 0) {
      _.set(filteredSearchObject, 'alliances', _.map(filteredSearchObject.alliances, (a) => a.value).join(','));
    }

    if (!filteredSearchObject.doShowRefundableFlightsOnly) {
      delete filteredSearchObject.doShowRefundableFlightsOnly;
    }
    if (!filteredSearchObject.doShowLongConnectFlights) {
      delete filteredSearchObject.doShowLongConnectFlights;
    }
    if (filteredSearchObject.doHideNdcFares === true) {
      _.set(filteredSearchObject, 'hideNDC', true);
    }
    if (!filteredSearchObject.filterTimeLayoverStart) {
      delete filteredSearchObject.filterTimeLayoverStart;
    }
    if (!filteredSearchObject.filterTimeLayoverEnd) {
      delete filteredSearchObject.filterTimeLayoverEnd;
    }

    delete filteredSearchObject.doHideNdcFares;
    delete filteredSearchObject.flightSearchType;
    delete filteredSearchObject.hasDifferentOutboundInboundCabins;
    delete filteredSearchObject.hasDifferentOutboundInboundAirLine;
    delete filteredSearchObject.hasDifferentOutboundInboundConnectingAirports;
    delete filteredSearchObject.hasDifferentOutboundInboundAlliance;
    delete filteredSearchObject.filterAirlines;
    delete filteredSearchObject.filterStops;
    delete filteredSearchObject.takeoff;
    delete filteredSearchObject.landing;
    delete filteredSearchObject.filterTakeoff;
    delete filteredSearchObject.filterLanding;
    delete filteredSearchObject.hasUserInteractedWithFlightList;
    delete filteredSearchObject.isNewSearch;

    if (filteredSearchObject.airlines) {
      _.set(filteredSearchObject, 'airlines', filteredSearchObject.airlines && _.map(filteredSearchObject.airlines, (a) => encodeAddressAutocompleteObj(a)));
    }

    return filteredSearchObject;
  }

  if (searchedMode === SearchModes.CAR) {
    const filteredSearchObject = {
      ...searchObj.car,
    };

    delete filteredSearchObject.defaultSearchObj;
    delete filteredSearchObject.responseFilters;

    if (_.isUndefined(filteredSearchObject.selectedCurrency)
    || _.isNull(filteredSearchObject.selectedCurrency)) {
      delete filteredSearchObject.selectedCurrency;
    }

    // remove empty staterooms
    _.set(filteredSearchObject, 'staterooms', encodeStateroomsObj(searchObj.staterooms));

    if (filteredSearchObject.pickupCode) {
      _.set(filteredSearchObject, 'pickupCode', encodeLocationTypeObj(filteredSearchObject.pickupCode));
    }

    if (filteredSearchObject.dropoffCode) {
      _.set(filteredSearchObject, 'dropoffCode', encodeLocationTypeObj(filteredSearchObject.dropoffCode));
    }

    if (filteredSearchObject.origin) {
      _.set(filteredSearchObject, 'origin', encodeGeonameObj(filteredSearchObject.origin));
    }
    return filteredSearchObject;
  }

  if (searchedMode === SearchModes.CUSTOM) {
    const key = 'customInventory';
    const copiedSearchObj = {
      ...searchObj[key],
    };
    const filteredSearchObject = _.pick(copiedSearchObj, CustomInventoryQueryStringFilterKeys);

    // remove empty staterooms
    _.set(filteredSearchObject, 'staterooms', encodeStateroomsObj(searchObj.staterooms));

    // remove unnecessary origin properties
    if (filteredSearchObject.origin) {
      _.set(filteredSearchObject, 'origin', encodeGeonameObj(filteredSearchObject.origin));
    }
    return filteredSearchObject;
  }

  if (searchedMode === SearchModes.TOUR) {
    const copiedSearchObj = {
      ...searchObj.tour,
    };
    const filteredSearchObject = _.pick(copiedSearchObj, TourQueryStringFilterKeys);

    // remove empty staterooms
    _.set(filteredSearchObject, 'staterooms', encodeStateroomsObj(searchObj.staterooms));

    // remove unnecessary origin properties
    if (filteredSearchObject.origin) {
      _.set(filteredSearchObject, 'origin', encodeGeonameObj(filteredSearchObject.origin));
    }
    return filteredSearchObject;
  }

  return {};
}
